import React, { useEffect, useRef, useState } from 'react';

import { css } from '../Viewer/lut';
import { OrientationLegend } from './CollagenCard';

const num = (v, digits) => v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
// Valeur d'une graduation : décimales selon l'ordre de grandeur, sans zéros inutiles.
export const tickLabel = v => {
  const a = Math.abs(v);
  return v.toLocaleString('en-US', { maximumFractionDigits: a >= 100 || a === 0 ? 0 : a >= 10 ? 1 : a >= 1 ? 2 : 3 });
};

// Barre de couleurs de la modalité affichée : { title, colorAt (t dans [0, 1] -> RGB 0–255), ticks [{ t, label }],
// note } ; { title, orientation: true } pour la légende des orientations du collagène ; { title, swatches [{ color,
// label }], line } pour un composite (une couleur par image) ou des isolignes (line : traits au lieu de carrés).
export function ColorBar({ legend }) {
  if (legend.swatches) {
    return (
      <div className="viewer-colorbar">
        <span className="viewer-colorbar-title">{legend.title}</span>
        {legend.swatches.map(({ color, label }) => (
          <span key={label} className={`viewer-colorbar-swatch${legend.line ? ' line' : ''}`}>
            <i style={{ background: css(color) }} />{label}
          </span>
        ))}
      </div>
    );
  }
  if (legend.orientation) {
    return (
      <div className="viewer-colorbar">
        <span className="viewer-colorbar-title">{legend.title}</span>
        <OrientationLegend />
      </div>
    );
  }
  const gradient = `linear-gradient(to right, ${Array.from({ length: 33 }, (_, i) => css(legend.colorAt(i / 32))).join(', ')})`;
  return (
    <div className="viewer-colorbar" title={legend.note || undefined}>
      <span className="viewer-colorbar-title">{legend.title}</span>
      <div className="viewer-colorbar-bar" style={{ background: gradient }} />
      <div className="viewer-colorbar-ticks">
        {legend.ticks.map(({ t, label }) => (
          <span key={t} style={{ left: `${t * 100}%` }} className={t === 0 ? 'first' : t === 1 ? 'last' : ''}>{label}</span>
        ))}
      </div>
    </div>
  );
}

// Longueur « ronde » (1, 2 ou 5 × 10^n) la plus proche de x.
function niceLength(x) {
  const p = 10 ** Math.floor(Math.log10(x));
  const m = x / p;
  return (m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10) * p;
}

// Barre d'échelle : longueur ronde d'environ 120 px d'écran, recalculée à chaque changement de vue (subscribe). umPerPx :
// taille d'un px pleine résolution en µm (null : inconnue, pas de barre) ; source : d'où elle vient. onSet
// (contributeurs) : clic pour la corriger (µm, vide : celle des fichiers).
export function ScaleBar({ umPerPx, source, getView, subscribe, onSet }) {
  const [, setTick] = useState(0);
  const [draft, setDraft] = useState(null);
  const editing = useRef(false);
  useEffect(() => subscribe(() => setTick(t => t + 1)), [subscribe]);
  const start = () => { editing.current = true; setDraft(umPerPx ? String(umPerPx) : ''); };
  const finish = commit => {
    if (!editing.current) return;
    editing.current = false;
    const value = Number(String(draft).replace(',', '.'));
    setDraft(null);
    if (commit) onSet(draft.trim() && value > 0 ? value : null);
  };
  if (draft != null) {
    return (
      <label className="viewer-scalebar editing">
        Pixel size
        <input value={draft} autoFocus inputMode="decimal" aria-label="Pixel size in µm" onFocus={e => e.target.select()}
          onChange={e => setDraft(e.target.value)} onBlur={() => finish(true)}
          onKeyDown={e => { if (e.key === 'Enter') finish(true); if (e.key === 'Escape') finish(false); }} />
        µm
      </label>
    );
  }
  if (!umPerPx) return null;
  const zoom = getView()?.zoom;
  if (zoom == null) return null;
  const pxPerUm = 2 ** (Array.isArray(zoom) ? zoom[0] : zoom) / umPerPx;  // px d'écran par µm
  const length = niceLength(120 / pxPerUm);
  const label = length >= 1000 ? `${tickLabel(length / 1000)} mm` : `${tickLabel(length)} µm`;
  const title = `1 px = ${num(umPerPx, 4)} µm (${source})${onSet ? ' · click to edit' : ''}`;
  return (
    <div className={`viewer-scalebar${onSet ? ' editable' : ''}`} title={title} onClick={onSet ? start : undefined}
      role={onSet ? 'button' : undefined}>
      <span>{label}</span>
      <div className="viewer-scalebar-bar" style={{ width: `${length * pxPerUm}px` }} />
    </div>
  );
}
